<template>
  <TransitionRoot as="template" :show="modalIsOpen">
    <Dialog class="relative z-10" @close="!(loading && loadingOverlay) && closedFromModal()">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-100 w-screen max-h-screen">
        <div class="flex min-h-full items-center justify-center text-center sm:items-center p-4 max-h-screen">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel :class="'relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 w-full ' + sizeClasses(size)">
              <div v-if="loading && loadingOverlay" class="absolute inset-0 z-20 flex cursor-wait items-center justify-center bg-white/70" role="status" aria-live="polite">
                <svg class="h-8 w-8 animate-spin text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-85" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 8l3-2.647z" />
                </svg>
                <span class="sr-only">Loading</span>
              </div>
              <div class="bg-white">
                <div class="mt-3">
                  <DialogTitle v-if="title || $slots.title " as="h3" class="m-6 md:mx-8">
                    <slot name="title">
                      {{ title }}
                    </slot>
                  </DialogTitle>
                  <div class="mt-2">
                    <div class="text-gray-900 max-h-[calc(100vh-180px)] overflow-y-auto m-6 md:mx-8 px-10]">
                      <slot name="default">
                        Modal Body
                      </slot>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!noFooter" class="bg-gray-50 p-4 flex flex-row-reverse">
                <tw-button :loading="loading" :disabled="okDisabled" :variant="variant" class="capitalize ml-2" @click="clickedOk">
                  <slot name="ok">
                    {{ okText }}
                  </slot>
                </tw-button>
                <tw-button v-if="!okOnly" variant="light" class="ml-2" @click="clickedCancel">
                  Cancel
                </tw-button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import TwButton from '@/components/generic/tw-button.vue'

defineOptions({ name: 'TwModal' })

const emit = defineEmits(['ok', 'cancel','opened','closed'])
defineProps({
  modalId: {
    type: String,
    required: false,
    default: function () {
      return 'tw-modal-' + Math.random().toString(36).substring(2, 12)
    }
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingOverlay: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: null
  },
  okDisabled: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: null
  },
  variant: {
    type: String,
    default: 'dark'
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  okText: {
    type: String,
    default: 'Ok'
  },
  noFooter: {
    type: Boolean,
    default: false
  },
  okOnly: {
    type: Boolean,
    default: false
  }
})

const sizeClasses = (size) => {
  switch(size) {
  case 'xs':
    return 'max-w-[24rem]'
  case 'sm':
    return 'max-w-[34rem]'
  case 'lg':
    return 'max-w-[60rem]'
  case 'xl':
    return 'max-w-[90rem]'
  default:
    return 'max-w-[44rem]'
  }
}

const modalIsOpen = ref(false)
const open = () => {
  modalIsOpen.value = true
  setTimeout(() => {
    emit('opened')
  }, 500)
}
const close = async () => {
  modalIsOpen.value = false
  emit('closed')
  console.log('close called')
}

const closedFromModal = () => {
  modalIsOpen.value = false
  emit('closed')
}

const clickedOk = () => {
  // we need to emit an event
  emit('ok')
}

const clickedCancel = () => {
  modalIsOpen.value = false
  emit('cancel')
}

defineExpose({
  open,
  close
})


</script>
